import { TaInput } from '@components/shared';
import { AuthHeader } from '@features/auth/AuthHeader';
import { PasswordRequirements } from '@features/auth/login/components/PasswordRequirements';
import { registrationInitialValues, registrationSchema } from '@features/auth/registration';
import { AuthPepper } from '@features/AuthPaper';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { FormHelperText } from '@mui/material';
import Button from '@mui/material/Button/Button';
import { useFormik } from 'formik';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Role, useRegister } from 'services/auth';

export const EmailAndPassword = () => {
	const { navigateWithLanguage } = useLanguageNavigate();
	const { t } = useTranslation();
	const { register, isLoading } = useRegister();

	const clearStore = () => {
		localStorage.removeItem('user');
		localStorage.removeItem('role');
	};

	const handleNextClick = async () => {
		const role = localStorage.getItem('role');

		if (formik.isValid) {
			try {
				await register({
					email: values.email,
					password: values.password,
					role: role as Role,
				});

				clearStore();
				navigateWithLanguage(`/${role}/personal-details`);
			} catch (error) {
				console.error('Registration failed:', error);
			}
		}
	};
	const formik = useFormik({
		initialValues: registrationInitialValues,
		validationSchema: registrationSchema,
		onSubmit: handleNextClick,
	});

	const { values, setValues } = formik;

	useEffect(() => {
		const role = localStorage.getItem('role');

		if (!role) navigateWithLanguage('/register/role-selection');

		const user = localStorage.getItem('user');

		if (user && role) {
			const parsedUser = JSON.parse(user);

			setValues({ role, repeatPassword: '', ...parsedUser });
		}
	}, [setValues, navigateWithLanguage]);

	const handleChange = useCallback(() => {
		const formValues = {
			...values,
			repeatPassword: '',
			password: '',
		};

		localStorage.setItem('user', JSON.stringify(formValues));
	}, [values]);

	return (
		<AuthPepper>
			<div className="mt-[50px]">
				<AuthHeader
					title={t('singUp')}
					spacing="mt-[80px] max-sm:mt-[50px]"
					wrapperSpacing="mb-[30px] max-sm:mb-[30px]"
				/>
			</div>
			<form
				className="max-w-[330px] !w-[330px] mx-auto mb-[80px] flex flex-col gap-[18px]"
				onSubmit={formik.handleSubmit}
				onChange={() => handleChange()}
			>
				<div>
					<TaInput
						icon={
							<EmailOutlinedIcon
								sx={{ color: formik.touched.email && formik.errors.email ? '#FD3333' : '#7B809A' }}
							/>
						}
						id="email"
						label={t('email')}
						size="small"
						value={formik.values.email}
						onChange={formik.handleChange}
						error={formik.errors.email}
						touched={formik.touched.email}
						isRequired
					/>
					{formik.touched.email && formik.errors.email && (
						<FormHelperText
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '9px',
								marginBottom: '4px',
								color: formik.touched.password && formik.errors.password ? '#FD3333' : '#7B809A',
							}}
						>
							<ErrorOutlineOutlinedIcon />
							{t(formik.errors.email)}
						</FormHelperText>
					)}
				</div>
				<div>
					<TaInput
						icon={
							<LockOutlinedIcon
								sx={{
									color: formik.touched.password && formik.errors.password ? '#FD3333' : '#7B809A',
								}}
							/>
						}
						id="password"
						label={t('password')}
						size="small"
						type="password"
						value={formik.values.password}
						onChange={formik.handleChange}
						error={formik.errors.password}
						touched={formik.touched.password}
						isRequired
					/>
					{(formik.values.password || (formik.touched.password && formik.errors.password)) && (
						<PasswordRequirements password={values.password} />
					)}
				</div>
				<div>
					<TaInput
						icon={
							<LockOutlinedIcon
								sx={{
									color:
										formik.touched.repeatPassword && formik.errors.repeatPassword
											? '#FD3333'
											: '#7B809A',
								}}
							/>
						}
						id="repeatPassword"
						label={t('repeatPassword')}
						size="small"
						type="password"
						value={formik.values.repeatPassword}
						onChange={formik.handleChange}
						error={formik.errors.repeatPassword}
						touched={formik.touched.repeatPassword}
						isRequired
					/>
					{formik.touched.repeatPassword && formik.errors.repeatPassword && (
						<FormHelperText
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '9px',
								marginBottom: '4px',
								color:
									formik.touched.repeatPassword && formik.errors.repeatPassword
										? '#FD3333'
										: '#7B809A',
							}}
						>
							<ErrorOutlineOutlinedIcon />
							{t(formik.errors.repeatPassword)}
						</FormHelperText>
					)}
				</div>
				<div className="mt-3 ">
					<Button
						classes={{ root: 'w-full' }}
						disabled={isLoading}
						type="submit"
						variant="contained"
					>
						{t('next')}
						<KeyboardArrowRightIcon />
					</Button>
				</div>
			</form>
		</AuthPepper>
	);
};
